/**
 * Annual commitment discount.
 *
 * A single constant rather than twelve hand-typed annual prices: hand-typed
 * ones drift, and a drifting discount is discovered by a customer rather than
 * by us.
 */
export const ANNUAL_DISCOUNT_RATE = 0.1;
/** Annual price for a monthly amount, at the current commitment discount. */
export function annualAmount(monthlyMinor, rate = ANNUAL_DISCOUNT_RATE) {
    return Math.round(monthlyMinor * 12 * (1 - rate));
}
/* ------------------------------------------------------------------ plans -- */
/*
  The grid of 28 September 2026, set by the owner:

    Voice Essentiel  249 € HT a month    400 minutes
    Voice Business   449 € HT a month  1 000 minutes, several collaborators, statistics
    Voice Pro        699 € HT a month  2 000 minutes, payments, and what comes next

  No setup fee on any of them: an installation that needs work is agreed with
  the establishment and set as a contract (ADR-0027). A minute beyond the
  allowance is 0,35 € HT on every plan.

  Answering, the FAQ, transferring a call, the call history, taking the
  appointment and the text that confirms it are the product itself: every plan
  has them, so they are not entitlements.

  Version 1 is written here rather than as a version 2 because the September
  grid was never published anywhere: no database holds a plan (checked on the
  production database on 28 September), and nothing of it could reach Stripe,
  whose prices are only ever created from the database's rows. A version that
  customers bought is never rewritten; this one had no customer and no price.
*/
const ESSENTIAL = {
    code: 'ESSENTIAL',
    name: 'Voice Essentiel',
    description: 'Le salon qui travaille seul : l’assistante répond, prend les rendez-vous, transfère.',
    isPublic: true,
    sortOrder: 10,
    versions: [
        {
            versionNumber: 1,
            status: 'active',
            notes: 'Grid of 28 September 2026.',
            prices: [
                { kind: 'base', billingInterval: 'monthly', amountMinor: 24_900 },
                { kind: 'base', billingInterval: 'annual', amountMinor: annualAmount(24_900) },
            ],
            features: [],
            // In the order a price card lists them.
            limits: {
                included_minutes: 400,
                // « Plusieurs collaborateurs » is what Voice Business adds: here the
                // diary holds one person, and the assistant books with that person.
                max_staff: 1,
                max_users: 2,
                max_locations: 1,
                max_phone_numbers: 1,
                max_concurrent_calls: 2,
                // Ceiling on what a tenant may configure. The tenant's own setting
                // still defaults to zero (charter §24).
                transcript_retention_days: 0,
            },
            overageRates: [{ usageType: 'VOICE_MINUTES', unitAmountMinor: 35 }],
        },
    ],
};
const BUSINESS = {
    code: 'BUSINESS',
    name: 'Voice Business',
    description: 'Le salon et son équipe : plus de minutes, plusieurs collaborateurs, les statistiques.',
    isPublic: true,
    sortOrder: 20,
    versions: [
        {
            versionNumber: 1,
            status: 'active',
            notes: 'Grid of 28 September 2026.',
            prices: [
                { kind: 'base', billingInterval: 'monthly', amountMinor: 44_900 },
                { kind: 'base', billingInterval: 'annual', amountMinor: annualAmount(44_900) },
            ],
            features: ['analytics_detailed', 'analytics_advanced', 'revenue_tracking'],
            limits: {
                included_minutes: 1_000,
                max_staff: 10,
                max_users: 10,
                max_locations: 1,
                max_phone_numbers: 2,
                max_concurrent_calls: 5,
                transcript_retention_days: 30,
            },
            overageRates: [{ usageType: 'VOICE_MINUTES', unitAmountMinor: 35 }],
        },
    ],
};
const PRO = {
    code: 'PRO',
    name: 'Voice Pro',
    description: 'Les gros volumes, le paiement à la réservation, et ce qui arrive ensuite.',
    isPublic: true,
    sortOrder: 30,
    versions: [
        {
            versionNumber: 1,
            status: 'active',
            notes: 'Grid of 28 September 2026.',
            prices: [
                { kind: 'base', billingInterval: 'monthly', amountMinor: 69_900 },
                { kind: 'base', billingInterval: 'annual', amountMinor: annualAmount(69_900) },
            ],
            // The last three are not built yet, and are sold as « bientôt
            // disponible », never as included today (see COMING_SOON in the site's
            // pricing). They are granted now so that whoever buys Pro on that
            // promise has them the day they exist, without a new version to move to.
            features: [
                'analytics_detailed',
                'analytics_advanced',
                'revenue_tracking',
                'payments_enabled',
                'multi_location',
                'advanced_workflows',
                'advanced_integrations',
            ],
            limits: {
                included_minutes: 2_000,
                max_staff: 50,
                max_users: 25,
                max_locations: 3,
                max_phone_numbers: 4,
                max_concurrent_calls: 10,
                transcript_retention_days: 90,
            },
            overageRates: [{ usageType: 'VOICE_MINUTES', unitAmountMinor: 35 }],
        },
    ],
};
/**
 * Enterprise carries **no price rows**: it is quote-only.
 *
 * Deliberate — putting an amount here would make an indicative figure behave
 * like a published price, and the first negotiated deal would immediately make
 * it wrong. Terms negotiated with one establishment are a contract (ADR-0027).
 */
const ENTERPRISE = {
    code: 'ENTERPRISE',
    name: 'Enterprise',
    description: 'Groupes, franchises, volumes importants, SLA et intégrations sur mesure.',
    isPublic: false,
    sortOrder: 40,
    versions: [
        {
            versionNumber: 1,
            status: 'active',
            notes: 'Template for negotiated contracts. Amounts are agreed per deal, not listed.',
            prices: [],
            features: [
                'payments_enabled',
                'analytics_detailed',
                'analytics_advanced',
                'revenue_tracking',
                'priority_support',
                'multi_location',
                'group_management',
                'custom_voice',
                'api_access',
                'custom_integrations',
                'sla',
                'advanced_workflows',
                'advanced_integrations',
            ],
            limits: {
                included_minutes: 2_500,
                max_locations: 10,
                max_phone_numbers: 20,
                max_concurrent_calls: 25,
                max_users: 100,
                max_staff: 250,
                transcript_retention_days: 365,
            },
            overageRates: [{ usageType: 'VOICE_MINUTES', unitAmountMinor: 35 }],
        },
    ],
};
export const LAUNCH_PLANS = [ESSENTIAL, BUSINESS, PRO, ENTERPRISE];
/* ---------------------------------------------------------------- coupons -- */
/**
 * The coupon that would waive a setup fee on an annual commitment.
 *
 * The checkout still honours it (`checkoutTerms`), and it is still read from
 * the database rather than restated. No public plan carries a setup fee since
 * the grid of 28 September 2026, so the catalogue publishes no such coupon: a
 * rule with nothing to apply to is a line a reader has to explain away.
 */
export const ANNUAL_COMMITMENT_COUPON = 'ANNUAL_SETUP_WAIVED';
/**
 * None today. The Founders offer (129 € a month, as 50 € off the September
 * Pro) was a price on a grid that no longer exists, and was never published.
 */
export const LAUNCH_COUPONS = [];
export const LAUNCH_CATALOGUE = {
    plans: LAUNCH_PLANS,
    coupons: LAUNCH_COUPONS,
};
//# sourceMappingURL=launch-catalogue.js.map